import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {
    ActivatedRoute,
    NavigationEnd,
    Router,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {filter, map, Observable, shareReplay, startWith, switchMap} from 'rxjs';
import {FeatureFlagService, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {getRouteLabel, getRouteTitle, visibleChildRoutes} from '@shared/route-utils';
import {selectUser} from 'src/app/state/selectors';
import {UserStateSuccess} from 'src/app/state/reducers';

type TabEntry = {
    path: string;
    label: string;
};

@Component({
    standalone: true,
    host: {class: 'd-block', style: '--step-sticky-offset: 4.5rem'},
    imports: [RouterLink, RouterLinkActive, RouterOutlet, TranslatePipe, AsyncPipe],
    template: `
        <ul class="nav nav-tabs justify-content-start pt-2 bg-white sticky-top">
            @for (tab of tabs$ | async; track tab.path) {
                <li class="nav-item">
                    <a class="nav-link" routerLinkActive="active" [routerLink]="tab.path">
                        {{ tab.label }}
                    </a>
                </li>
            }
        </ul>

        @if (pageTitle$ | async; as pageTitle) {
            <div class="container-xxl mx-0">
                <h1 class="mt-3 mb-2">{{ pageTitle }}</h1>
            </div>
        }

        <router-outlet />
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportComponent {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly store = inject(Store);

    protected readonly tabs$: Observable<TabEntry[]> = this.store.select(selectUser).pipe(
        filter((state): state is UserStateSuccess => state.status === 'success'),
        switchMap(state =>
            visibleChildRoutes(
                this.route.routeConfig?.children ?? [],
                state.user.buildingServices,
                this.featureFlagService,
            ),
        ),
        map(routes =>
            routes.map(route => ({
                path: route.path ?? '',
                label: getRouteLabel(route, this.translate),
            })),
        ),
        shareReplay({bufferSize: 1, refCount: true}),
    );

    private readonly navigationEnd$ = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
    );

    protected readonly pageTitle$: Observable<string | null> = this.navigationEnd$.pipe(
        switchMap(() => this.tabs$),
        map(tabs =>
            tabs.length === 0
                ? this.translate.translate('quickexports_export_page_title', 'online', 'export')
                : getRouteTitle(this.route.firstChild?.snapshot, this.translate),
        ),
    );

    constructor() {
        this.navigationEnd$
            .pipe(
                switchMap(() => this.tabs$),
                filter(() => this.route.children.length === 0),
                filter(tabs => tabs.length > 0),
                takeUntilDestroyed(),
            )
            .subscribe(tabs => {
                void this.router.navigate([tabs[0].path], {
                    relativeTo: this.route,
                    replaceUrl: true,
                });
            });
    }
}
